import { z } from "zod";
import { MEAL_REQUIREMENTS, type MealRequirement } from "./accreditation-write";
import type { Keyset, Paginated } from "./envelope";
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_MAX } from "./pagination";

/**
 * M12 Food & Beverages (migration 0037, PLAN `~/.claude/plans/m12-fnb/PLAN.md`
 * §0 F1–F13): dining halls, the meal plan, meal-pass scans at the kitchen
 * door, and takeaway (packed-meal) orders. Wire shapes only (snake_case).
 *
 *   F1  Meal entitlement = the card holds an ACTIVE access code of kind MEAL.
 *   F2  Diets are `people.food_type` (MEAL_REQUIREMENTS) minus what every
 *       Games kitchen already is (halal, so also no pork): FNB_DIETS.
 *   F3  A lodging building eats at ONE hall per games; the hall and its
 *       buildings are of one cluster.
 *   F4  One timetable per hall: breakfast / lunch / dinner, wall-clock in the
 *       games' time zone, the same every day, never overnight.
 *   F5  The meal plan is computed, never stored (one statement per day).
 *   F6  A scan is one statement; one meal per person per session, across
 *       every hall (partial unique index).
 *   F7  Takeaway orders: TAKEAWAY_ORDER_TRANSITIONS, enforced by the trigger
 *       `fn_takeaway_order_status_guard` (a unit test holds them equal).
 *
 * Enum members are VALUES, never keys (`ok()` lower-cases all-caps keys), so
 * every count is `{ status | meal | diet, count }[]`.
 */

// ── Vocabulary ──────────────────────────────────────────────────────────────

/** The three services of a day, in time order. `meal_scans.meal`, `dining_hall_sessions.meal`, `takeaway_orders.meal`. */
export const MEALS = ["BREAKFAST", "LUNCH", "DINNER"] as const;
export type Meal = (typeof MEALS)[number];

/** `takeaway_orders.meal_rank` (GENERATED): the "meal time" sort within a day. */
export const MEAL_RANK = { BREAKFAST: 1, LUNCH: 2, DINNER: 3 } as const satisfies Record<Meal, number>;

/** What every Games kitchen already is (F2): never a diet a hall has to list. */
export const FNB_KITCHEN_STANDARD = ["HALAL", "NO_PORK"] as const satisfies readonly MealRequirement[];

/** The diets a hall may serve and the meal plan counts: MEAL_REQUIREMENTS minus FNB_KITCHEN_STANDARD (derived, never re-typed). */
export type FnbDiet = Exclude<MealRequirement, (typeof FNB_KITCHEN_STANDARD)[number]>;
export const FNB_DIETS = MEAL_REQUIREMENTS.filter((d): d is FnbDiet => !(FNB_KITCHEN_STANDARD as readonly string[]).includes(d)) as unknown as readonly [
	FnbDiet,
	...FnbDiet[],
];

/** A scan's verdict. Every scan of a known card is logged, refused ones too. */
export const MEAL_SCAN_OUTCOMES = ["OK", "REFUSED"] as const;
export type MealScanOutcome = (typeof MEAL_SCAN_OUTCOMES)[number];

/**
 * Why a scan was refused, checked in this order:
 *   CARD_NOT_ACTIVE   the card is not APPROVED, or its physical state is not ACTIVE
 *   NO_MEAL_ACCESS    the card holds no active MEAL access code
 *   NOT_FOR_THIS_HALL the hall is limited to some NOCs / categories, and this card is not one of them (2026-11-03)
 *   HALL_NOT_SERVING  no session of the hall is open now (games time zone), or the hall is inactive
 *   ALREADY_SERVED    this person was already served this meal today, at any hall
 */
export const MEAL_SCAN_REFUSALS = ["CARD_NOT_ACTIVE", "NO_MEAL_ACCESS", "NOT_FOR_THIS_HALL", "HALL_NOT_SERVING", "ALREADY_SERVED"] as const;
export type MealScanRefusal = (typeof MEAL_SCAN_REFUSALS)[number];

/** A session of the meal plan's day against the viewer's "now" in the games' time zone. */
export const MEAL_SESSION_STATES = ["DONE", "OPEN", "LATER"] as const;
export type MealSessionState = (typeof MEAL_SESSION_STATES)[number];

/** `takeaway_orders.status` (`ck_takeaway_orders_status`). A new order is PENDING (NOC) or CONFIRMED (host). */
export const TAKEAWAY_ORDER_STATUSES = ["PENDING", "CONFIRMED", "REJECTED", "CANCELLED", "COLLECTED"] as const;
export type TakeawayOrderStatus = (typeof TAKEAWAY_ORDER_STATUSES)[number];

/** The statuses an order may be created in (`fn_takeaway_order_status_guard`, INSERT). */
export const TAKEAWAY_ORDER_INITIAL_STATUSES = ["PENDING", "CONFIRMED"] as const satisfies readonly TakeawayOrderStatus[];

/**
 * The status graph (F7). `fn_takeaway_order_status_guard` (0037) refuses any
 * other move with 23514 `ck_takeaway_orders_status_transition`
 * (→ 409 INVALID_STATUS_TRANSITION), psql included; tests/unit/
 * takeaway-order-guard.test.ts holds the two equal. Change both in one migration.
 */
export const TAKEAWAY_ORDER_TRANSITIONS = {
	PENDING: ["CONFIRMED", "REJECTED", "CANCELLED"],
	CONFIRMED: ["COLLECTED", "CANCELLED"],
	REJECTED: [],
	CANCELLED: [],
	COLLECTED: [],
} as const satisfies Record<TakeawayOrderStatus, readonly TakeawayOrderStatus[]>;

export const takeawayOrderTransitionPairs = (): Array<[TakeawayOrderStatus, TakeawayOrderStatus]> =>
	TAKEAWAY_ORDER_STATUSES.flatMap((from) =>
		(TAKEAWAY_ORDER_TRANSITIONS[from] as readonly TakeawayOrderStatus[]).map((to): [TakeawayOrderStatus, TakeawayOrderStatus] => [from, to]),
	);
export const takeawayOrderCanMove = (from: TakeawayOrderStatus, to: TakeawayOrderStatus): boolean =>
	(TAKEAWAY_ORDER_TRANSITIONS[from] as readonly TakeawayOrderStatus[]).includes(to);
/** The statuses an order can be moved TO (the targets of the status routes). */
export const TAKEAWAY_ORDER_TARGETS = ["CONFIRMED", "REJECTED", "CANCELLED", "COLLECTED"] as const satisfies readonly TakeawayOrderStatus[];
export type TakeawayOrderTarget = (typeof TAKEAWAY_ORDER_TARGETS)[number];
/** The statuses whose move needs a reason (`status_note`): the audit keeps the history. */
export const TAKEAWAY_NOTE_REQUIRED = ["REJECTED", "CANCELLED"] as const satisfies readonly TakeawayOrderTarget[];
/** The statuses whose packs are subtracted from the meal plan's expected count (F5). */
export const TAKEAWAY_COUNTED_STATUSES = ["CONFIRMED", "COLLECTED"] as const satisfies readonly TakeawayOrderStatus[];
/** Still waiting on someone: the "Needs attention" count. */
export const TAKEAWAY_OPEN_STATUSES = ["PENDING"] as const satisfies readonly TakeawayOrderStatus[];

/**
 * Why a bulk status move left an order out:
 *   not_found           not an order this login can see
 *   forbidden           it can see it, but may not take this move (a NOC only cancels)
 *   invalid_transition  the order's status has no edge to the target
 *   meal_started        a NOC cancel after the meal started (TAKEAWAY_MEAL_STARTED)
 *   changed             it changed between the read and the write (retry)
 */
export const TAKEAWAY_BULK_SKIP_REASONS = ["not_found", "forbidden", "invalid_transition", "meal_started", "changed"] as const;
export type TakeawayBulkSkipReason = (typeof TAKEAWAY_BULK_SKIP_REASONS)[number];

/** `GET /takeaway-orders` sorts: meal time (date, meal, pick-up time — ascending), newest first, most packs first. */
export const TAKEAWAY_SORT_KEYS = ["meal_time", "recent", "packs"] as const;
export type TakeawaySortKey = (typeof TAKEAWAY_SORT_KEYS)[number];

/** Who reads a takeaway / F&B screen: the host's, or a NOC's own (decided by the ROLE: ORGANIZATION → noc). */
export const FNB_VIEWS = ["host", "noc"] as const;
export type FnbView = (typeof FNB_VIEWS)[number];

// ── Limits ──────────────────────────────────────────────────────────────────

export const DINING_HALL_NAME_MAX = 120;
export const DINING_HALL_ADDRESS_MAX = 300;
export const DINING_HALL_PHONE_MAX = 30;
export const DINING_HALL_SEATS_MIN = 10;
export const DINING_HALL_SEATS_MAX = 20_000;
/** Buildings one hall may serve (one write). */
export const DINING_HALL_BUILDINGS_MAX = 100;
/** Organisations / categories one hall may be limited to (empty = open to everyone). */
export const DINING_HALL_ORGS_MAX = 60;
export const DINING_HALL_FUNCTIONS_MAX = 50;
/** `GET /dining-halls/options` returns at most this many (and `GET /dining-halls` pages at most PAGE_SIZE_MAX). */
export const DINING_HALL_OPTIONS_MAX = 200;

export const TAKEAWAY_PACKS_MAX = 500;
export const TAKEAWAY_BULK_MAX = 100;
export const TAKEAWAY_REASON_MAX = 500;
export const TAKEAWAY_NOTE_MAX = 500;
export const FNB_SEARCH_MAX = 100;
/** Keyset cursors: Postgres' full-precision text inside, never a JS Date (bug 6). */
export const FNB_CURSOR_MAX = 300;

/**
 * `fnb_settings` per games (F11). A games with no row reads the defaults (the
 * SQL COALESCEs to the same numbers; contract-parity.fnb.test.ts pins them).
 *   takeaway_cutoff_hours  an order placed less than this before the meal starts is LATE (a flag, never a block)
 *   seat_turn_minutes      how long one seating takes: turns per session = session minutes / this
 */
export const FNB_SETTINGS_RANGES = {
	takeaway_cutoff_hours: { min: 0, max: 168, default: 24 },
	seat_turn_minutes: { min: 10, max: 240, default: 45 },
} as const;

/**
 * The meal plan's day chips (F13): the games' dates widened by these many
 * days (arrivals before the opening, departures after the close). A games
 * with no dates yet: today ± `no_dates_days`. Any date may still be asked for.
 */
export const FNB_PLAN_RANGE = { before_start_days: 7, after_end_days: 3, no_dates_days: 7 } as const;

/** `HH:MM`, 00:00–23:59 (a hall session, a pick-up time). */
export const FNB_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// ── Formatting ──────────────────────────────────────────────────────────────

/** `TO-000123`: an order's `ref_no` (from `takeaway_order_ref_seq`) as printed everywhere. */
export const formatTakeawayRef = (refNo: number | string): string => `TO-${String(refNo).padStart(6, "0")}`;

/** `TO-000123`, `to-123`, `000123`, `123` → 123. Anything else → null (the search then tries the organisation name). */
export function parseTakeawayRefQuery(q: string): number | null {
	const m = /^(?:TO-?)?0*([0-9]{1,12})$/i.exec(q.trim());
	if (!m) return null;
	const n = Number(m[1]);
	return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Minutes of a session (`HH:MM` – `HH:MM`, same day). */
export const sessionMinutes = (startsAt: string, endsAt: string): number => {
	const m = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
	return m(endsAt) - m(startsAt);
};

/**
 * F5: guests to seat per turn. Turns = session minutes / seat-turn minutes,
 * at least one (a session shorter than one seating still seats everyone
 * once). Mirrors the SQL in the meal plan.
 */
export const perTurn = (expected: number, minutes: number, seatTurnMinutes: number): number =>
	Math.ceil(expected / Math.max(1, minutes / seatTurnMinutes));

// ── Messages (message-factory pattern, web namespace `fnb`) ─────────────────

export interface FnbFormMessages {
	required: string;
	textTooLong: string;
	/** `HH:MM`. */
	invalidTime: string;
	/** A hall needs breakfast, lunch and dinner, each once. */
	sessionsAllMeals: string;
	/** A session ends after it starts (no overnight). */
	sessionEndsAfterStart: string;
	duplicateDiet: string;
	duplicateBuilding: string;
	seatsRange: string;
	packsRange: string;
	/** The diet packs add up to more than the order's packs. */
	dietPacksOver: string;
	/** Reject / Cancel need a reason. */
	noteRequired: string;
	/** A scan sends a QR token OR a card number. */
	scanTarget: string;
	nothingToChange: string;
	duplicateId: string;
	/** A NOC / category picked twice in a hall's limits. */
	duplicateChoice: string;
}

export const enFnbFormMessages: FnbFormMessages = {
	required: "Required",
	textTooLong: "Too long",
	invalidTime: "Use HH:MM",
	sessionsAllMeals: "Set breakfast, lunch and dinner",
	sessionEndsAfterStart: "Must end after it starts",
	duplicateDiet: "Each diet once",
	duplicateBuilding: "Each building once",
	seatsRange: `Between ${DINING_HALL_SEATS_MIN} and ${DINING_HALL_SEATS_MAX}`,
	packsRange: `Between 1 and ${TAKEAWAY_PACKS_MAX}`,
	dietPacksOver: "The diet packs add up to more than the packs ordered",
	noteRequired: "Give a reason",
	scanTarget: "Scan a card or type its number",
	nothingToChange: "Nothing to change",
	duplicateId: "Each order once",
	duplicateChoice: "Each one once",
};

type FM = FnbFormMessages;

const uuid = z.string().uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
const hhmm = (m: FM) => z.string().trim().regex(FNB_TIME_PATTERN, m.invalidTime);
/** Optional text: trimmed FIRST, then ≤ max; blank, null or omitted → null. */
const nullableText = (m: FM, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.nullable()
		.optional()
		.transform((v) => (v === undefined || v === null || v === "" ? null : v));
/** PATCH text: omitted stays omitted (unchanged); trimmed FIRST, then ≤ max; blank or null → null (cleared). */
const patchText = (m: FM, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.nullable()
		.optional()
		.transform((v) => (v === "" ? null : v));
/** A yes/no query parameter: exactly `true` / `false` — never `z.coerce.boolean()` (bug 2). */
const booleanParam = z.enum(["true", "false"]).transform((v) => v === "true");
/** `a,b,c` off the query string → a de-duplicated list of `values`. */
const enumList = <T extends readonly [string, ...string[]]>(values: T) =>
	z
		.string()
		.max(200)
		.transform((v) =>
			v
				.split(",")
				.map((s) => s.trim())
				.filter((s) => s.length > 0),
		)
		.pipe(z.array(z.enum(values)).min(1))
		.transform((list) => [...new Set(list)]);
const distinct = (xs: readonly string[]) => new Set(xs.map((x) => x.toLowerCase())).size === xs.length;

// ── Settings ────────────────────────────────────────────────────────────────

const R = FNB_SETTINGS_RANGES;

/** `GET /fnb/settings?games_id=`. */
export const fnbSettingsQuery = z.object({ games_id: uuid });

/** `PUT /fnb/settings` — both values, upserted for the games. */
export const putFnbSettingsBody = z.object({
	games_id: uuid,
	takeaway_cutoff_hours: z.number().int().min(R.takeaway_cutoff_hours.min).max(R.takeaway_cutoff_hours.max),
	seat_turn_minutes: z.number().int().min(R.seat_turn_minutes.min).max(R.seat_turn_minutes.max),
});
export type PutFnbSettingsBody = z.input<typeof putFnbSettingsBody>;

// ── Dining halls ────────────────────────────────────────────────────────────

/** One session of a hall's timetable. */
export function makeDiningHallSession(m: FM = enFnbFormMessages) {
	return z
		.object({ meal: z.enum(MEALS), starts_at: hhmm(m), ends_at: hhmm(m) })
		.refine((s) => s.ends_at > s.starts_at, { message: m.sessionEndsAfterStart, path: ["ends_at"] });
}

/** All three meals, each once (F4). */
const sessionsField = (m: FM) =>
	z
		.array(makeDiningHallSession(m))
		.length(MEALS.length, m.sessionsAllMeals)
		.refine((ss) => MEALS.every((meal) => ss.some((s) => s.meal === meal)), { message: m.sessionsAllMeals });
const dietsField = (m: FM) =>
	z
		.array(z.enum(FNB_DIETS))
		.max(FNB_DIETS.length)
		.refine((ds) => distinct(ds), m.duplicateDiet);
const buildingsField = (m: FM) =>
	z
		.array(uuid)
		.max(DINING_HALL_BUILDINGS_MAX)
		.refine((ids) => distinct(ids), m.duplicateBuilding);
/** A hall's limits: who may eat there. Empty = everyone (the default). */
const limitField = (m: FM, max: number) =>
	z
		.array(uuid)
		.max(max)
		.refine((ids) => distinct(ids), m.duplicateChoice);
const seatsField = (m: FM) => z.number().int().min(DINING_HALL_SEATS_MIN, m.seatsRange).max(DINING_HALL_SEATS_MAX, m.seatsRange);

/**
 * `POST /dining-halls` — the hall, its three sessions, the diets it serves and
 * the lodging buildings that eat there, written in ONE statement. A building
 * already served by another hall of the games → 409 BUILDING_HAS_HALL; a
 * building of another cluster → 422 HALL_CLUSTER_MISMATCH.
 */
export function makeCreateDiningHallBody(m: FM = enFnbFormMessages) {
	return z.object({
		games_id: uuid,
		cluster_id: uuid,
		name: z.string().trim().min(1, m.required).max(DINING_HALL_NAME_MAX, m.textTooLong),
		address: nullableText(m, DINING_HALL_ADDRESS_MAX),
		phone: nullableText(m, DINING_HALL_PHONE_MAX),
		seats: seatsField(m),
		is_active: z.boolean().default(true),
		sessions: sessionsField(m),
		diets: dietsField(m).default([]),
		lodging_building_ids: buildingsField(m).default([]),
		/** Only these NOCs may eat here; [] = every organisation. */
		responsible_organization_ids: limitField(m, DINING_HALL_ORGS_MAX).default([]),
		/** Only these categories may eat here; [] = every category. */
		main_function_ids: limitField(m, DINING_HALL_FUNCTIONS_MAX).default([]),
	});
}
export const createDiningHallBody = makeCreateDiningHallBody();
export type CreateDiningHallBody = z.input<typeof createDiningHallBody>;

/**
 * `PATCH /dining-halls/:id` — any field; `sessions`, `diets` and
 * `lodging_building_ids` REPLACE the whole set when sent. `version` from the
 * read (→ 409 STALE_VERSION). The games never changes.
 */
export function makePatchDiningHallBody(m: FM = enFnbFormMessages) {
	return z
		.object({
			version: z.number().int().min(1),
			cluster_id: uuid.optional(),
			name: z.string().trim().min(1, m.required).max(DINING_HALL_NAME_MAX, m.textTooLong).optional(),
			address: patchText(m, DINING_HALL_ADDRESS_MAX),
			phone: patchText(m, DINING_HALL_PHONE_MAX),
			seats: seatsField(m).optional(),
			is_active: z.boolean().optional(),
			sessions: sessionsField(m).optional(),
			diets: dietsField(m).optional(),
			lodging_building_ids: buildingsField(m).optional(),
			responsible_organization_ids: limitField(m, DINING_HALL_ORGS_MAX).optional(),
			main_function_ids: limitField(m, DINING_HALL_FUNCTIONS_MAX).optional(),
		})
		.refine((v) => Object.entries(v).some(([k, x]) => k !== "version" && x !== undefined), { message: m.nothingToChange });
}
export const patchDiningHallBody = makePatchDiningHallBody();
export type PatchDiningHallBody = z.input<typeof patchDiningHallBody>;

/** `GET /dining-halls` — offset pages (a small list), name order. */
export const listDiningHallsQuery = z.object({
	games_id: uuid,
	active: booleanParam.optional(),
	page: z.coerce.number().int().min(1).default(1),
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_MAX),
});
export type ListDiningHallsQuery = z.output<typeof listDiningHallsQuery>;
export type ListDiningHallsQueryInput = z.input<typeof listDiningHallsQuery>;

/** `GET /dining-halls/options` — active halls of the games (the scan station's picker, the order form). */
export const diningHallOptionsQuery = z.object({ games_id: uuid });

/**
 * `GET /dining-halls/building-options?games_id=` — the hall form's "Serves
 * these buildings" picker, for the hall writers (admins, `fnb.manage`) even
 * without accommodation permissions: every lodging building (≤
 * LODGING_BUILDINGS_LIMIT, name order) with its cluster and the hall of THIS
 * games that serves it, if any.
 */
export const diningHallBuildingOptionsQuery = z.object({ games_id: uuid });

// ── Meal plan ───────────────────────────────────────────────────────────────

/** `GET /fnb/meal-plan?games_id=&date=` — date omitted = today in the games' time zone. */
export const mealPlanQuery = z.object({ games_id: uuid, date: isoDate.optional() });
export type MealPlanQuery = z.output<typeof mealPlanQuery>;

/** `GET /fnb/noc?games_id=&date=` — a NOC's own overview. */
export const fnbNocQuery = mealPlanQuery;

// ── Scans ───────────────────────────────────────────────────────────────────

/**
 * `POST /meal-scans` — the kitchen door: the hall the scanner stands at, and
 * EITHER the QR token read off the card OR a typed card number (`000123`,
 * `ACC-THA-000123`, … — `parseCardNumberQuery`).
 */
export function makeMealScanBody(m: FM = enFnbFormMessages) {
	return z
		.object({
			dining_hall_id: uuid,
			qr_token: z.string().trim().min(1).max(64).optional(),
			card_no: z.string().trim().min(1).max(40).optional(),
		})
		.refine((v) => (v.qr_token === undefined) !== (v.card_no === undefined), { message: m.scanTarget, path: ["card_no"] });
}
export const mealScanBody = makeMealScanBody();
export type MealScanBody = z.input<typeof mealScanBody>;

/** `GET /meal-scans` — keyset on `(scanned_at, meal_scan_id) DESC`; first page carries the total. `date` omitted = today in the games' time zone (the list is always one service day). */
export const listMealScansQuery = z.object({
	games_id: uuid,
	dining_hall_id: uuid.optional(),
	date: isoDate.optional(),
	meal: z.enum(MEALS).optional(),
	outcome: z.enum(MEAL_SCAN_OUTCOMES).optional(),
	cursor: z.string().min(1).max(FNB_CURSOR_MAX).optional(),
	limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});
export type ListMealScansQuery = z.output<typeof listMealScansQuery>;
export type ListMealScansQueryInput = z.input<typeof listMealScansQuery>;

// ── Takeaway orders ─────────────────────────────────────────────────────────

/**
 * `GET /takeaway-orders` — keyset. `q` = a reference (`TO-000123`) or an
 * organisation name. `status` omitted = every status. `late=true` = only the
 * late ones. Status chip counts and the total come with the first page.
 */
export const listTakeawayOrdersQuery = z.object({
	games_id: uuid,
	q: z.string().trim().min(1).max(FNB_SEARCH_MAX).optional(),
	status: enumList(TAKEAWAY_ORDER_STATUSES).optional(),
	late: booleanParam.optional(),
	dining_hall_id: uuid.optional(),
	responsible_organization_id: uuid.optional(),
	date: isoDate.optional(),
	meal: z.enum(MEALS).optional(),
	sort: z.enum(TAKEAWAY_SORT_KEYS).default("meal_time"),
	cursor: z.string().min(1).max(FNB_CURSOR_MAX).optional(),
	limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});
export type ListTakeawayOrdersQuery = z.output<typeof listTakeawayOrdersQuery>;
export type ListTakeawayOrdersQueryInput = z.input<typeof listTakeawayOrdersQuery>;

/**
 * `POST /takeaway-orders`. The status is decided by the caller: a host (an
 * admin, or a group with `fnb.manage` over the organisation) creates
 * CONFIRMED; a NOC (`fnb.order`) creates PENDING, and not once the meal has
 * started (422 TAKEAWAY_MEAL_STARTED). Diet packs add up to at most `packs`.
 */
export function makeCreateTakeawayOrderBody(m: FM = enFnbFormMessages) {
	return z
		.object({
			games_id: uuid,
			responsible_organization_id: uuid,
			dining_hall_id: uuid,
			service_date: isoDate,
			meal: z.enum(MEALS),
			packs: z.number().int().min(1, m.packsRange).max(TAKEAWAY_PACKS_MAX, m.packsRange),
			pickup_time: hhmm(m),
			reason: nullableText(m, TAKEAWAY_REASON_MAX),
			diets: z
				.array(z.object({ diet: z.enum(FNB_DIETS), packs: z.number().int().min(1, m.packsRange).max(TAKEAWAY_PACKS_MAX, m.packsRange) }))
				.max(FNB_DIETS.length)
				.refine((ds) => distinct(ds.map((d) => d.diet)), m.duplicateDiet)
				.default([]),
		})
		.refine((v) => v.diets.reduce((s, d) => s + d.packs, 0) <= v.packs, { message: m.dietPacksOver, path: ["diets"] });
}
export const createTakeawayOrderBody = makeCreateTakeawayOrderBody();
export type CreateTakeawayOrderBody = z.input<typeof createTakeawayOrderBody>;

const noteFor = (m: FM) => nullableText(m, TAKEAWAY_NOTE_MAX);
const noteGiven = (v: { to: TakeawayOrderTarget; note: string | null }) => !(TAKEAWAY_NOTE_REQUIRED as readonly string[]).includes(v.to) || v.note !== null;

/** `POST /takeaway-orders/:id/status` — one order, with the `version` the client read. */
export function makeTakeawayStatusBody(m: FM = enFnbFormMessages) {
	return z
		.object({ to: z.enum(TAKEAWAY_ORDER_TARGETS), note: noteFor(m), version: z.number().int().min(1) })
		.refine(noteGiven, { message: m.noteRequired, path: ["note"] });
}
export const takeawayStatusBody = makeTakeawayStatusBody();
export type TakeawayStatusBody = z.input<typeof takeawayStatusBody>;

/** `POST /takeaway-orders/bulk/status` — ≤ TAKEAWAY_BULK_MAX orders, one guarded UPDATE; the rest are skipped with a reason. */
export function makeTakeawayBulkStatusBody(m: FM = enFnbFormMessages) {
	return z
		.object({
			ids: z
				.array(uuid)
				.min(1)
				.max(TAKEAWAY_BULK_MAX)
				.refine((ids) => distinct(ids), m.duplicateId),
			to: z.enum(TAKEAWAY_ORDER_TARGETS),
			note: noteFor(m),
		})
		.refine(noteGiven, { message: m.noteRequired, path: ["note"] });
}
export const takeawayBulkStatusBody = makeTakeawayBulkStatusBody();
export type TakeawayBulkStatusBody = z.input<typeof takeawayBulkStatusBody>;

// ── Responses ───────────────────────────────────────────────────────────────

export interface FnbUserRef {
	user_id: string;
	name: string;
}
export interface FnbHallRef {
	dining_hall_id: string;
	name: string;
}
export interface FnbOrgRef {
	responsible_organization_id: string;
	/** The country's IOC code of a NOC-type organisation, else null. */
	code: string | null;
	name: string;
}
export interface FnbClusterRef {
	cluster_id: string;
	code: string;
	name: string;
}

/** `GET`/`PUT /fnb/settings`. `updated_at` null = no row yet (the defaults). */
export interface FnbSettings {
	games_id: string;
	takeaway_cutoff_hours: number;
	seat_turn_minutes: number;
	updated_at: string | null;
}

export interface DiningHallSession {
	meal: Meal;
	/** `HH:MM`, wall-clock in the games' time zone. */
	starts_at: string;
	ends_at: string;
}

/** One hall (`GET /dining-halls` row, a write's answer). Sessions in MEALS order. */
export interface DiningHall {
	dining_hall_id: string;
	games_id: string;
	cluster: FnbClusterRef;
	name: string;
	address: string | null;
	phone: string | null;
	seats: number;
	is_active: boolean;
	sessions: DiningHallSession[];
	diets: FnbDiet[];
	buildings: Array<{ lodging_building_id: string; name: string }>;
	/** Who may eat here (2026-11-03); both empty = everyone. A card needs its NOC listed (or none listed) AND its category listed (or none listed). */
	organizations: Array<{ responsible_organization_id: string; code: string | null; name: string }>;
	main_functions: Array<{ main_function_id: string; code: string; name: string }>;
	version: number;
	created_at: string;
	updated_at: string;
}

export type DiningHallsPage = Paginated<DiningHall>;

/** One row of `GET /dining-halls/building-options`. `served_by` = the hall of the asked games serving it (another hall's → 409 BUILDING_HAS_HALL on save). */
export interface DiningHallBuildingOption {
	lodging_building_id: string;
	name: string;
	cluster_id: string;
	is_active: boolean;
	served_by: FnbHallRef | null;
}

/** `GET /dining-halls/options`. */
export interface DiningHallOption extends FnbHallRef {
	cluster: FnbClusterRef;
	sessions: DiningHallSession[];
}

/** One diet's count within a session (diets with nobody are left out). */
export interface MealPlanDietCount {
	diet: FnbDiet;
	count: number;
	/** The hall lists this diet. False = people need it and the hall does not serve it ("Needs attention"). */
	served: boolean;
}

/** One hall × meal of the meal plan's day (F5). */
export interface MealPlanSession extends DiningHallSession {
	state: MealSessionState;
	/** Entitled people staying in the hall's buildings for this meal (breakfast: the night before; dinner: the night of; lunch: either). */
	present: number;
	/** Packs of CONFIRMED + COLLECTED takeaway orders for this hall, date and meal. */
	takeaway_packs: number;
	/** present − takeaway packs, never below 0. */
	expected: number;
	/** OK scans so far at this hall for this meal. */
	served: number;
	/** expected / turns (session minutes / seat_turn_minutes, at least 1), rounded up. */
	per_turn: number;
	over_capacity: boolean;
	diets: MealPlanDietCount[];
}

export interface MealPlanHall {
	dining_hall_id: string;
	name: string;
	cluster: FnbClusterRef;
	seats: number;
	diets: FnbDiet[];
	buildings: Array<{ lodging_building_id: string; name: string }>;
	sessions: MealPlanSession[];
}

/** `GET /fnb/meal-plan` (host: admins and `fnb.manage`). One statement. */
export interface MealPlan {
	view: "host";
	games_id: string;
	date: string;
	/** Today in the games' time zone, and the wall-clock time there now (`HH:MM`). */
	today: string;
	now_time: string;
	timezone: string;
	/** The day chips (FNB_PLAN_RANGE). */
	days: { from: string; to: string };
	settings: Pick<FnbSettings, "takeaway_cutoff_hours" | "seat_turn_minutes">;
	/** Active halls of the games, name order. */
	halls: MealPlanHall[];
	/** The stat strip: every hall, per meal. */
	totals: Array<{ meal: Meal; present: number; takeaway_packs: number; expected: number; served: number }>;
	attention: {
		/** PENDING orders of the games (every date). */
		pending_orders: number;
		/** PENDING orders for the plan's date. */
		pending_orders_on_date: number;
		over_capacity: Array<{ dining_hall_id: string; name: string; meal: Meal; per_turn: number; seats: number }>;
		/** A diet some of a hall's people need that the hall does not list. `count` = people (any meal of the day). */
		diets_not_served: Array<{ dining_hall_id: string; name: string; diet: FnbDiet; count: number }>;
		/** People staying in a building a hall serves, with an APPROVED card but no MEAL access. */
		no_meal_access: number;
		/** Lodging buildings with guests that night that no hall of the games serves. */
		buildings_without_hall: Array<{ lodging_building_id: string; name: string; people: number }>;
	};
}

/** `GET /fnb/noc` (ORGANIZATION with `fnb.order`): where the delegation eats + its own orders. Never another organisation's numbers. */
export interface FnbNocOverview {
	view: "noc";
	games_id: string;
	date: string;
	today: string;
	timezone: string;
	takeaway_cutoff_hours: number;
	/** Halls serving buildings where the caller's own people hold an open bed that night (or the night before). */
	halls: Array<
		FnbHallRef & {
			address: string | null;
			phone: string | null;
			cluster: FnbClusterRef;
			sessions: DiningHallSession[];
			diets: FnbDiet[];
			/** Only the buildings where its own people stay, with how many of them. */
			buildings: Array<{ lodging_building_id: string; name: string; people: number }>;
			people: number;
		}
	>;
	/** Its own organisations' orders of the games, per status. */
	order_counts: Array<{ status: TakeawayOrderStatus; count: number }>;
}

/** The card a scan resolved (the scanner's screen). */
export interface MealScanCard {
	ac_card_id: string;
	registration_no: number;
	registration_no_display: string;
	/** `000123` — what is printed on the card face. */
	card_number: string;
	printed_name: string;
	photo_url: string | null;
	organization: FnbOrgRef;
	category: { main_function_id: string; code: string; name: string };
	/** Everything on `people.food_type` (halal included) — what the person asked for. */
	diets: MealRequirement[];
}

/** `POST /meal-scans` → 201, OK or REFUSED; both are logged. Unknown card (or out of scope) → 404 CARD_NOT_FOUND, nothing logged. */
export interface MealScanResult {
	meal_scan_id: string;
	outcome: MealScanOutcome;
	reason: MealScanRefusal | null;
	/** The open session's meal; null when the hall is not serving. */
	meal: Meal | null;
	service_date: string;
	scanned_at: string;
	dining_hall: FnbHallRef;
	card: MealScanCard;
	/** The hall serving the building the person sleeps in tonight (else last night); null = none. Eating elsewhere is allowed. */
	home_hall: FnbHallRef | null;
	/** ALREADY_SERVED: where and when the meal was served. Null otherwise. */
	first_scan: { dining_hall: FnbHallRef; scanned_at: string } | null;
}

/** One row of `GET /meal-scans`. */
export interface MealScanListItem {
	meal_scan_id: string;
	scanned_at: string;
	service_date: string;
	meal: Meal | null;
	outcome: MealScanOutcome;
	reason: MealScanRefusal | null;
	dining_hall: FnbHallRef;
	card: { ac_card_id: string; registration_no: number; card_number: string; printed_name: string; organization: FnbOrgRef };
	scanned_by: FnbUserRef | null;
}

export interface MealScansPage extends Keyset<MealScanListItem> {
	/** First page only, else null. */
	total: number | null;
}

/** An order as a NOC sees it (`view: "noc"`): no host user is ever named. */
export interface TakeawayOrderNocItem {
	takeaway_order_id: string;
	ref_no: number;
	/** `TO-000123`. */
	ref: string;
	games_id: string;
	organization: FnbOrgRef;
	dining_hall: FnbHallRef;
	service_date: string;
	meal: Meal;
	/** The hall's session start for the meal (`HH:MM`, games time zone). */
	meal_starts_at: string | null;
	pickup_time: string;
	packs: number;
	diets: Array<{ diet: FnbDiet; packs: number }>;
	reason: string | null;
	status: TakeawayOrderStatus;
	/** The reason given for a REJECTED / CANCELLED move. */
	status_note: string | null;
	/** Ordered less than `takeaway_cutoff_hours` before the meal starts (a flag only). */
	late: boolean;
	/** The meal has started (games time zone): a NOC can no longer cancel. */
	meal_started: boolean;
	created_at: string;
	decided_at: string | null;
	version: number;
	/** The moves THIS viewer may take now (from the same predicates the writes use). */
	next_statuses: TakeawayOrderTarget[];
}

/** An order as the host sees it (`view: "host"`). */
export interface TakeawayOrderItem extends TakeawayOrderNocItem {
	created_by: FnbUserRef | null;
	decided_by: FnbUserRef | null;
}

export type TakeawayOrdersPage =
	| ({ view: "host" } & Keyset<TakeawayOrderItem> & TakeawayPageCounts)
	| ({ view: "noc" } & Keyset<TakeawayOrderNocItem> & TakeawayPageCounts);

export interface TakeawayPageCounts {
	/** First page only (null after): rows matching every filter. */
	total: number | null;
	/** First page only: per status over the same filters EXCEPT `status` (the chips). */
	counts: Array<{ status: TakeawayOrderStatus; count: number }> | null;
	/** First page only: late orders over the same filters except `status` and `late`. */
	late_count: number | null;
}

/** `GET /takeaway-orders/:id`, a create's or a status move's answer. */
export type TakeawayOrderView = ({ view: "host" } & TakeawayOrderItem) | ({ view: "noc" } & TakeawayOrderNocItem);

export interface TakeawayBulkStatusResult {
	updated: string[];
	skipped: Array<{ takeaway_order_id: string; reason: TakeawayBulkSkipReason }>;
}
